import React from 'react';
import { StoryblokCommon } from '../../../lib/storyblok/storyblok';
import { NavigationItemTopStoryblok, NavigationLinkStoryblok, TopNavigationStoryblok } from '../../../_shared/interfaces/storyblok';
import NavigationMenuDesktop from './navigation-menu-desktop';
import Headroom from 'react-headroom';
import { Link } from 'gatsby';
import AccountContext from '../../../context/account/account.context';
import NavigationMenuMobile from './navigation-menu-mobile';

import * as StylesHs from './navigation-header.hs.module.scss';
import * as StylesDr from './navigation-header.dr.module.scss';
import { getMultilinkURL } from '../../../lib/helpers/helpers';

const NavigationHeader = ({ blok, storyblokCommon }: { blok: TopNavigationStoryblok; storyblokCommon: StoryblokCommon }) => {
    const accountContext = React.useContext(AccountContext);
    const [activeNavigationItem, _setActiveNavigationItem] = React.useState<
        NavigationItemTopStoryblok | TopNavigationStoryblok | undefined
    >();
    const [logoImage, setLogoImage] = React.useState<string | undefined>();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState<boolean>(false);
    const activeNavigationItemRef = React.useRef(activeNavigationItem);
    const isHSTheme = storyblokCommon.configuration.content.theme === 'hs';
    const styles = storyblokCommon.configuration.content.theme === 'dr' ? StylesDr : StylesHs;
    const isWindow = typeof window !== 'undefined';
    const navigationId = 'navigation';

    import('../../../images/header/' + storyblokCommon.configuration.content.theme + '/logo.svg')
        .then((image) => {
            if (image?.default) {
                setLogoImage(image.default);
            }
        })
        .catch(() => {
            //
        });

    React.useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'visible';
        }
    }, [isMobileMenuOpen]);

    React.useEffect(() => {
        if (typeof accountContext.error === 'string') {
            if (accountContext.error.includes('AADB2C90118')) {
                accountContext.forgotPassword(storyblokCommon.configuration.content.language, storyblokCommon.configuration.content.theme);
            }
            if (accountContext.error.includes('AADB2C90091')) {
                accountContext.updateToken();
            }
        }
    }, [accountContext.error]);

    React.useEffect(() => {
        if (isWindow) {
            document.addEventListener('keydown', pressEscCheck, false);
            window.addEventListener('scroll', handleScroll);
            document.addEventListener('mousedown', handlePageClick);
            window.addEventListener('resize', handleResize);

            return () => {
                document.removeEventListener('keydown', pressEscCheck, false);
                window.removeEventListener('scroll', handleScroll);
                document.removeEventListener('mousedown', handlePageClick);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, []);

    const pressEscCheck = React.useCallback((event) => {
        if (event.keyCode === 27) {
            resetActiveNavigationItem();
        }
    }, []);

    const handleScroll = React.useCallback(() => {
        resetActiveNavigationItem();
    }, []);

    const handlePageClick = (e: MouseEvent) => {
        if (activeNavigationItemRef.current) {
            if (e.target) {
                // @ts-ignore
                const targetId: string = e.target.id ?? '';
                // @ts-ignore
                const parentElementId: string = e.target.closest('ul')?.id ?? '';
                // if click was not on submenu-button and not inside submenu-layer: close layer
                if (
                    !targetId.startsWith('link') &&
                    !targetId.startsWith('button') &&
                    !targetId.startsWith(navigationId) &&
                    !parentElementId.startsWith(navigationId)
                ) {
                    resetActiveNavigationItem();
                }
            }
        }
    };

    const handleResize = React.useCallback(() => {
        setIsMobileMenuOpen(false);
        resetActiveNavigationItem();
    }, []);

    const setActiveNavigationItem = (navigationItem: NavigationItemTopStoryblok | TopNavigationStoryblok) => {
        if (activeNavigationItem?._uid === navigationItem._uid) {
            activeNavigationItemRef.current = undefined;
        } else {
            activeNavigationItemRef.current = navigationItem;
        }
        _setActiveNavigationItem(activeNavigationItemRef.current);
    };

    const resetActiveNavigationItem = () => {
        activeNavigationItemRef.current = undefined;
        _setActiveNavigationItem(activeNavigationItemRef.current);
    };

    const showNavigationItemHeader = (): React.ReactFragment => {
        return (
            <>
                {blok?.links?.map((navigationItemTop: NavigationLinkStoryblok | NavigationItemTopStoryblok) => {
                    if (navigationItemTop.component === 'navigation_item_top') {
                        return (
                            <div key={navigationItemTop._uid} className={styles.navigationItemTopHeadline}>
                                <button
                                    id={navigationId}
                                    className={
                                        activeNavigationItem?._uid === navigationItemTop._uid ? styles.navigationItemHeadlineActive : ''
                                    }
                                    aria-haspopup="true"
                                    aria-controls={navigationId}
                                    aria-expanded={activeNavigationItem?._uid === navigationItemTop._uid}
                                    onClick={() => {
                                        setActiveNavigationItem(navigationItemTop ?? '');
                                    }}
                                >
                                    {navigationItemTop.text}
                                </button>
                            </div>
                        );
                    }
                })}
            </>
        );
    };

    const showNavigationItemCustomer = (): React.ReactFragment => {
        return (
            <div className={styles.navigationItemTopCustomer}>
                <button
                    id={navigationId}
                    className={
                        activeNavigationItem?._uid === storyblokCommon.topNavigation.content._uid ? styles.navigationItemHeadlineActive : ''
                    }
                    aria-haspopup="true"
                    aria-controls={navigationId}
                    aria-expanded={activeNavigationItem?._uid === storyblokCommon.topNavigation.content._uid}
                    onClick={() => {
                        setActiveNavigationItem(storyblokCommon.topNavigation.content);
                    }}
                >
                    {isHSTheme ? (
                        <>
                            {accountContext.account ? (
                                <span className={styles.iconUser}>{storyblokCommon.topNavigation.content.navigation_text}</span>
                            ) : (
                                <span className={styles.iconKey}>{storyblokCommon.topNavigation.content.navigation_text_log_in}</span>
                            )}
                        </>
                    ) : (
                        <span className={styles.iconUser}>{storyblokCommon.topNavigation.content.navigation_text}</span>
                    )}
                </button>
            </div>
        );
    };

    return (
        <Headroom>
            <header className={styles.header}>
                <div>
                    {/*LOGO START*/}
                    <Link to={getMultilinkURL(storyblokCommon.topNavigation.content.home_link)} className={styles.logo} aria-label={''}>
                        {logoImage && (
                            <img
                                src={logoImage}
                                alt={storyblokCommon.topNavigation.content.home_aria_label}
                                width={storyblokCommon.configuration.content.theme === 'hs' ? '285' : '176'}
                                height={storyblokCommon.configuration.content.theme === 'hs' ? '55' : '74'}
                            />
                        )}
                    </Link>
                    {/*LOGO END*/}

                    {/*NAVIGATION START*/}
                    <div className={[styles.navigationWrapper, isMobileMenuOpen ? styles.mobileMenuIsOpen : ''].join(' ')}>
                        {/*OPEN MENU START*/}
                        <>
                            <div
                                className={
                                    !isMobileMenuOpen || !activeNavigationItem ? styles.hideMobileNavigation : styles.showMobileNavigation
                                }
                            >
                                {/*OPEN MENU MOBILE START*/}
                                <NavigationMenuMobile
                                    blok={activeNavigationItem}
                                    navigationItems={!isMobileMenuOpen && activeNavigationItem ? undefined : blok?.links}
                                    storyblokCommon={storyblokCommon}
                                    accountContext={accountContext}
                                    onClose={() => {
                                        resetActiveNavigationItem();
                                    }}
                                />
                                {/*OPEN MENU MOBILE END*/}
                            </div>
                            {!isMobileMenuOpen && (
                                <>
                                    {/*OPEN MENU DESKTOP START*/}
                                    <NavigationMenuDesktop
                                        blok={activeNavigationItem}
                                        navigationItems={activeNavigationItem && blok?.links}
                                        storyblokCommon={storyblokCommon}
                                        accountContext={accountContext}
                                    />
                                    {/*OPEN MENU DESKTOP END*/}
                                </>
                            )}
                        </>
                        {/*OPEN MENU END*/}

                        {/*MENU HEADLINE ITEMS START*/}
                        {((isMobileMenuOpen && !activeNavigationItem) || !isMobileMenuOpen) && (
                            <nav role="menu">
                                {/*MENU ITEMS START*/}
                                <div className={styles.navigationItemHeader}>{showNavigationItemHeader()}</div>
                                {/*MENU ITEMS END*/}

                                {/*MENU CUSTOMER START*/}
                                <div className={styles.navigationItemCustomer}> {showNavigationItemCustomer()}</div>
                                {/*MENU CUSTOMER END*/}
                            </nav>
                        )}
                        {/*MENU HEADLINE ITEMS END*/}
                    </div>
                    {/*NAVIGATION END*/}

                    {/*BURGER ICON START*/}
                    <button
                        onClick={() => {
                            setIsMobileMenuOpen(!isMobileMenuOpen);
                        }}
                        className={[styles.mobileButton, isMobileMenuOpen ? styles.isCloseButton : ''].join(' ')}
                        aria-label="Menu"
                    />
                    {/*BURGER ICON END*/}
                </div>
            </header>
        </Headroom>
    );
};

export default NavigationHeader;
